import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {styled} from '@mui/material';
import CCOContainer from '../../fundamentals/CCOContainer';
import PreviewElementBoxDrawer from '../../preview/PreviewElementBoxDrawer';
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from '../../../context/PreviewContext';
import useWindowSize from '../../../utility/useWindowSize';
import CCOResponsivePicture from '../../fundamentals/CCOResponsivePicture';
import {C014StageCarouselPictureSettings} from '../../../common/constants/pictureSettings';
import {getAltText} from '../../fundamentals/CCOPicture';
import {getLengthLimit} from '../../../utility/lengthLimits';
import {usePreviewLimitCheck} from '../../preview/usePreviewLimitCheck';
import {SosTeaserType} from "../../types";
import CCOAiGeneratedImageNotice from "../../fundamentals/CCOAiGeneratedImageNotice";

export interface Props {
    item?: SosTeaserType;
}
const HeroImage = styled('div')({
    alignItems: 'end',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    textAlign: 'left',
});

const ImageOverlay = styled('div')({
    position: 'absolute',
});

const textStyle = {
    '& span': {
        background: 'white',
        boxDecorationBreak: 'clone',
        fontSize: {lg: '2.25rem', xl: '2.75rem'},
        lineHeight: {xs: '2.1rem', md: '2.7rem', xl: '3.5rem'},
        marginY: 0,
        paddingX: {xs: '16px', sm: '24px', md: '16px'},
        paddingY: '5px',
    },
    margin: 0,
    padding: 0,
};

const C082CenteredStageTeaser: React.FC<Props> = ({item}) => {
    const {isPreview} = usePreviewContext();
    const {
        greaterThanWindowMedium,
        isWindowExtraSmall,
        isWindowLarge,
        isWindowMedium,
        isWindowSmall,
        lowerThanWindowMedium,
    } = useWindowSize();

    const TITLE_CHAR_LIMIT = getLengthLimit('C082CenteredStageTeaser.title');

    usePreviewLimitCheck({
        id: item?.id,
        component: 'C082CenteredStageTeaser',
        field: 'teaserTitle',
        value: item?.teaserTitle || '',
        limit: TITLE_CHAR_LIMIT,
        source: 'default',
    });

    const isReady = !!(item && item.picture);
    if (!isReady) return null;

    const getHeroImageHeight = () => {
        if (isWindowExtraSmall) return '330px';
        if (isWindowSmall) return '338px';
        if (isWindowMedium) return '386px';
        if (isWindowLarge) return '514px';
        return '540px';
    };

    const getMarginBottom = () => {
        if (isWindowExtraSmall || isWindowSmall) return '28px';
        if (isWindowMedium) return '32px';
        if (isWindowLarge) return '44px';
        return '52px';
    };

    const heroImageHeight = getHeroImageHeight();
    const marginBottom = getMarginBottom();

    const truncatedTeaserTitle = item?.teaserTitle && item.teaserTitle.length > 150
        ? `${item.teaserTitle.slice(0, 150)}...`
        : item?.teaserTitle || '';

    const renderTextContent = (isHeadlineOverlay: boolean) => (
        <Box sx={{
            marginBottom: isHeadlineOverlay ? marginBottom : 2,
            marginTop: isHeadlineOverlay ? 0 : 2,
            width: {xs: '100%', lg: '928px'},
            zIndex: '0',
        }}>
            <CCOContainer width='full'>
                <Box sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'center',
                    marginY: '0',
                    paddingX: {xs: 0, md: 3, lg: 0},
                    width: '100%',
                }}>
                    <Typography
                        component={item?.teaserSettings?.activateH1 ? 'h1' : 'div'}
                        sx={textStyle}
                        variant='h3'
                    >
                        <span {...metaDataProperty(isPreview, 'properties.teaserTitle',
                            item.id)}>
                            {truncatedTeaserTitle}
                        </span>
                    </Typography>
                </Box>
            </CCOContainer>
        </Box>
    );

    return (
        <PreviewElementBoxDrawer propertyId={item.id}>
            <HeroImage style={{height: heroImageHeight}}>
                <CCOResponsivePicture
                    alt={getAltText(item.picture!.alt, item.picture!.title)}
                    hasPriority
                    imageStyle={{height: '100%', objectFit: 'cover', width: '100%'}}
                    picture={item.picture!}
                    pictureSetting={C014StageCarouselPictureSettings}
                    pictureStyle={{height: '100%', position: 'absolute', width: '100%'}}
                />
                <ImageOverlay/>
                {greaterThanWindowMedium && renderTextContent(true)}
            </HeroImage>
            {lowerThanWindowMedium && renderTextContent(false)}
            <CCOAiGeneratedImageNotice picture={item.picture}/>
        </PreviewElementBoxDrawer>
    );
};

export default C082CenteredStageTeaser;
